import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space,
  Spin, message, Table, Modal, Form, Input,
  DatePicker, TimePicker, Select, Tabs, Alert,
  Empty, Badge, Tooltip, Divider, Upload, Image, Avatar,
  Radio, Progress
} from 'antd';
import {
  CheckSquareOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  TagOutlined,
  EditOutlined,
  SwapOutlined,
  PlusOutlined,
  SyncOutlined,
  LinkOutlined,
  UploadOutlined,
  PaperClipOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  UserOutlined,
  HourglassOutlined,
  SettingOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FireOutlined,
  SmileOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api, { API_BASE_URL } from '../api';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Status definitions matching thinktech_apk (ActivityScreen.js, MeetingScreen.js, TicketScreen.js)
const ACTIVITY_STATUS_OPTIONS = [
  { label: 'Schedule', value: 'SCHEDULE', color: '#6B7280', bg: '#F3F4F6', icon: '📅' },
  { label: 'In Progress', value: 'IN_PROGRESS', color: '#125EC9', bg: '#EBF3FF', icon: '⚙️' },
  { label: 'Review', value: 'REVIEW', color: '#D97706', bg: '#FFFBEB', icon: '🔍' },
  { label: 'Done', value: 'DONE', color: '#059669', bg: '#ECFDF5', icon: '✅' },
];

const MEETING_STATUS_OPTIONS = [
  { label: 'Schedule', value: 'SCHEDULE', color: '#6366F1', bg: '#EEF2FF', icon: '📅' },
  { label: 'In Progress', value: 'IN_PROGRESS', color: '#F59E0B', bg: '#FFFBEB', icon: '⚙️' },
  { label: 'Done', value: 'DONE', color: '#10B981', bg: '#ECFDF5', icon: '✅' },
];

const TICKET_STATUS_OPTIONS = [
  { label: 'Schedule', value: 'SCHEDULE', color: '#6366F1', bg: '#EEF2FF', icon: '📅' },
  { label: 'In Progress', value: 'IN_PROGRESS', color: '#F59E0B', bg: '#FFFBEB', icon: '⚙️' },
  { label: 'Review', value: 'REVIEW', color: '#8B5CF6', bg: '#F5F3FF', icon: '🔍' },
  { label: 'Done', value: 'DONE', color: '#10B981', bg: '#ECFDF5', icon: '✅' },
];

const PRIORITY_OPTIONS = [
  { label: 'Low', value: 'LOW', color: '#10B981', bg: '#ECFDF5' },
  { label: 'Medium', value: 'MEDIUM', color: '#F59E0B', bg: '#FFFBEB' },
  { label: 'High', value: 'HIGH', color: '#EF4444', bg: '#FEF2F2' },
];

const StaffTasks = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activities'); // Default to Activities (clean actionable workspace)
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Data states
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Filters
  const [activityFilter, setActivityFilter] = useState('ALL');
  const [activitySearch, setActivitySearch] = useState('');
  const [meetingFilter, setMeetingFilter] = useState('ALL');
  const [meetingSearch, setMeetingSearch] = useState('');
  const [ticketFilter, setTicketFilter] = useState('ALL');
  const [ticketSearch, setTicketSearch] = useState('');

  // Modals: Activity
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [isEditActivity, setIsEditActivity] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [activitySubmitting, setActivitySubmitting] = useState(false);
  const [activityForm] = Form.useForm();

  // Status modal: Activity
  const [activityStatusModalVisible, setActivityStatusModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityStatusForm] = Form.useForm();

  // Transfer modal: Activity
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferringActivity, setTransferringActivity] = useState(null);
  const [targetUserId, setTargetUserId] = useState(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  // Modals: Meeting
  const [meetingModalVisible, setMeetingModalVisible] = useState(false);
  const [isEditMeeting, setIsEditMeeting] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [meetingSubmitting, setMeetingSubmitting] = useState(false);
  const [meetingForm] = Form.useForm();

  // Status modal: Meeting
  const [meetingStatusModalVisible, setMeetingStatusModalVisible] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingStatusForm] = Form.useForm();

  // Modals: Ticket
  const [ticketModalVisible, setTicketModalVisible] = useState(false);
  const [isEditTicket, setIsEditTicket] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketForm] = Form.useForm();
  const [ticketFile, setTicketFile] = useState(null);

  // Status modal: Ticket
  const [ticketStatusModalVisible, setTicketStatusModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketStatusForm] = Form.useForm();

  // Detail modal: Ticket
  const [ticketDetailModalVisible, setTicketDetailModalVisible] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);

  // Load current user from storage
  useEffect(() => {
    try {
      const uStr = sessionStorage.getItem('impersonate_user') || localStorage.getItem('user');
      if (uStr) setCurrentUser(JSON.parse(uStr));
    } catch (_) {}
  }, []);

  // Fetch all task data (matching TodoListScreen.js)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [actRes, meetRes, tickRes, staffRes] = await Promise.all([
        api.get('/activities/me').catch(() => ({ data: { activities: [] } })),
        api.get('/meetings/me').catch(() => ({ data: { meetings: [] } })),
        api.get('/tickets/my').catch(() => ({ data: [] })),
        api.get('/tickets/staff').catch(() => ({ data: [] }))
      ]);

      // Activities
      if (actRes.data?.success && Array.isArray(actRes.data?.activities)) {
        setActivities(actRes.data.activities);
      } else if (Array.isArray(actRes.data)) {
        setActivities(actRes.data);
      }

      // Meetings
      if (meetRes.data?.success && Array.isArray(meetRes.data?.meetings)) {
        setMeetings(meetRes.data.meetings);
      } else if (Array.isArray(meetRes.data)) {
        setMeetings(meetRes.data);
      }

      // Tickets
      if (Array.isArray(tickRes.data)) {
        setTickets(tickRes.data);
      } else if (Array.isArray(tickRes.data?.tickets)) {
        setTickets(tickRes.data.tickets);
      }

      // Staff list for allocation / transfer
      if (Array.isArray(staffRes.data)) {
        setStaffList(staffRes.data);
      } else if (Array.isArray(staffRes.data?.staff)) {
        setStaffList(staffRes.data.staff);
      }
    } catch (err) {
      console.error('Fetch tasks error:', err);
      message.error('Failed to load tasks and activities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Current month filter (matching TodoListScreen.js lines 25-38)
  const currentMonthPrefix = dayjs().format('YYYY-MM');

  const monthActivities = useMemo(() => {
    return activities.filter((a) => {
      const d = a.date || a.createdAt;
      return d && String(d).startsWith(currentMonthPrefix);
    });
  }, [activities, currentMonthPrefix]);

  const monthMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const d = m.scheduledAt || m.createdAt;
      return d && String(d).startsWith(currentMonthPrefix);
    });
  }, [meetings, currentMonthPrefix]);

  const monthTickets = useMemo(() => {
    return tickets.filter((t) => {
      const d = t.createdAt || t.created_at;
      return d && String(d).startsWith(currentMonthPrefix);
    });
  }, [tickets, currentMonthPrefix]);

  // Counts for status grids (matching TodoListScreen.js)
  const activityCounts = useMemo(() => ({
    SCHEDULE: monthActivities.filter((a) => a.status === 'SCHEDULE').length,
    IN_PROGRESS: monthActivities.filter((a) => a.status === 'IN_PROGRESS').length,
    REVIEW: monthActivities.filter((a) => a.status === 'REVIEW').length,
    DONE: monthActivities.filter((a) => a.status === 'DONE').length,
  }), [monthActivities]);

  const meetingCounts = useMemo(() => ({
    SCHEDULE: monthMeetings.filter((m) => (m.status || 'SCHEDULE') === 'SCHEDULE').length,
    IN_PROGRESS: monthMeetings.filter((m) => m.status === 'IN_PROGRESS').length,
    DONE: monthMeetings.filter((m) => m.status === 'DONE').length,
  }), [monthMeetings]);

  const ticketCounts = useMemo(() => ({
    SCHEDULE: monthTickets.filter((t) => t.status === 'SCHEDULE').length,
    IN_PROGRESS: monthTickets.filter((t) => t.status === 'IN_PROGRESS').length,
    REVIEW: monthTickets.filter((t) => t.status === 'REVIEW').length,
    DONE: monthTickets.filter((t) => t.status === 'DONE').length,
  }), [monthTickets]);

  // Overall completion rate
  const completionRate = useMemo(() => {
    const total = monthActivities.length;
    if (total === 0) return 100;
    const completed = activityCounts.DONE;
    return Math.round((completed / total) * 100);
  }, [monthActivities.length, activityCounts.DONE]);

  // Filtered lists for each tab
  const filteredActivities = useMemo(() => {
    return activities.filter((a) => {
      const matchFilter = activityFilter === 'ALL' || a.status === activityFilter;
      const q = activitySearch.toLowerCase().trim();
      const matchSearch = !q || (a.title && a.title.toLowerCase().includes(q)) || (a.description && a.description.toLowerCase().includes(q));
      return matchFilter && matchSearch;
    });
  }, [activities, activityFilter, activitySearch]);

  const filteredMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const matchFilter = meetingFilter === 'ALL' || (m.status || 'SCHEDULE') === meetingFilter;
      const q = meetingSearch.toLowerCase().trim();
      const matchSearch = !q || (m.title && m.title.toLowerCase().includes(q)) || (m.description && m.description.toLowerCase().includes(q));
      return matchFilter && matchSearch;
    });
  }, [meetings, meetingFilter, meetingSearch]);

  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      const matchFilter = ticketFilter === 'ALL' || t.status === ticketFilter;
      const q = ticketSearch.toLowerCase().trim();
      const matchSearch = !q || (t.title && t.title.toLowerCase().includes(q)) || (t.description && t.description.toLowerCase().includes(q));
      return matchFilter && matchSearch;
    });
  }, [tickets, ticketFilter, ticketSearch]);

  // -----------------------------------------------------------------
  // 1. ACTIVITY HANDLERS (Matching ActivityScreen.js)
  // -----------------------------------------------------------------
  const handleOpenCreateActivity = () => {
    setIsEditActivity(false);
    setEditingActivityId(null);
    activityForm.resetFields();
    activityForm.setFieldsValue({
      turnAroundDate: dayjs().add(1, 'day'),
      turnAroundTime: dayjs().set('hour', 17).set('minute', 0)
    });
    setActivityModalVisible(true);
  };

  const handleOpenEditActivity = (act) => {
    setIsEditActivity(true);
    setEditingActivityId(act.id);
    activityForm.resetFields();
    activityForm.setFieldsValue({
      title: act.title || '',
      description: act.description || '',
      remarks: act.remarks || '',
      turnAroundDate: act.turnAroundDate ? dayjs(act.turnAroundDate) : null,
      turnAroundTime: act.turnAroundTime ? dayjs(act.turnAroundTime, 'hh:mm A') : null
    });
    setActivityModalVisible(true);
  };

  const handleSaveActivity = async (values) => {
    try {
      setActivitySubmitting(true);
      const payload = {
        title: values.title.trim(),
        description: values.description.trim(),
        remarks: values.remarks || '',
        turnAroundDate: values.turnAroundDate ? values.turnAroundDate.format('YYYY-MM-DD') : null,
        turnAroundTime: values.turnAroundTime ? values.turnAroundTime.format('hh:mm A') : '',
        status: isEditActivity ? undefined : 'SCHEDULE',
        date: isEditActivity ? undefined : dayjs().format('YYYY-MM-DD')
      };

      if (isEditActivity) {
        await api.patch(`/activities/${editingActivityId}`, payload);
        message.success('Activity updated successfully');
      } else {
        await api.post('/activities', payload);
        message.success('Activity created successfully');
      }

      setActivityModalVisible(false);
      activityForm.resetFields();
      fetchData();
    } catch (err) {
      console.error('Save activity error:', err);
      message.error(err.response?.data?.message || 'Failed to save activity');
    } finally {
      setActivitySubmitting(false);
    }
  };

  const handleOpenUpdateActivityStatus = (act) => {
    setSelectedActivity(act);
    activityStatusForm.resetFields();
    activityStatusForm.setFieldsValue({
      status: act.status || 'SCHEDULE',
      remarks: act.remarks || ''
    });
    setActivityStatusModalVisible(true);
  };

  const handleSubmitActivityStatus = async (values) => {
    if (!selectedActivity) return;
    try {
      setActivitySubmitting(true);
      await api.patch(`/activities/${selectedActivity.id}/status`, {
        status: values.status,
        remarks: values.remarks || ''
      });
      message.success(`Status updated to ${values.status}`);
      setActivityStatusModalVisible(false);
      fetchData();
    } catch (err) {
      console.error('Update status error:', err);
      message.error(err.response?.data?.message || 'Failed to update activity status');
    } finally {
      setActivitySubmitting(false);
    }
  };

  const handleOpenTransferActivity = (act) => {
    setTransferringActivity(act);
    setTargetUserId(null);
    setTransferModalVisible(true);
  };

  const handleSubmitTransferActivity = async () => {
    if (!transferringActivity || !targetUserId) {
      message.error('Please select a staff member to transfer');
      return;
    }
    try {
      setTransferSubmitting(true);
      await api.patch(`/activities/${transferringActivity.id}/transfer`, {
        targetUserId
      });
      message.success('Activity shared successfully');
      setTransferModalVisible(false);
      fetchData();
    } catch (err) {
      console.error('Transfer activity error:', err);
      message.error(err.response?.data?.message || 'Failed to share activity');
    } finally {
      setTransferSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // 2. MEETING HANDLERS (Matching MeetingScreen.js)
  // -----------------------------------------------------------------
  const handleOpenCreateMeeting = () => {
    setIsEditMeeting(false);
    setEditingMeetingId(null);
    meetingForm.resetFields();
    meetingForm.setFieldsValue({
      scheduledAt: dayjs().add(1, 'hour')
    });
    setMeetingModalVisible(true);
  };

  const handleOpenEditMeeting = (m) => {
    setIsEditMeeting(true);
    setEditingMeetingId(m.id);
    meetingForm.resetFields();
    meetingForm.setFieldsValue({
      title: m.title || '',
      description: m.description || '',
      meetLink: m.meetLink || '',
      scheduledAt: m.scheduledAt ? dayjs(m.scheduledAt) : dayjs().add(1, 'hour'),
      attendees: Array.isArray(m.attendees) ? m.attendees.map(a => a.id || a.userId) : []
    });
    setMeetingModalVisible(true);
  };

  const handleSaveMeeting = async (values) => {
    try {
      const link = values.meetLink?.trim();
      const gmeetRegex = /^(https?:\/\/)?meet\.google\.com\/[a-z0-9-]+$/i;
      if (!link || !gmeetRegex.test(link)) {
        message.error('Please enter a valid Google Meet link (e.g. meet.google.com/abc-defg-hij)');
        return;
      }

      setMeetingSubmitting(true);
      const payload = {
        title: values.title.trim(),
        description: values.description || '',
        meetLink: link,
        scheduledAt: values.scheduledAt ? values.scheduledAt.toISOString() : new Date().toISOString(),
        attendees: values.attendees || []
      };

      if (isEditMeeting) {
        await api.put(`/meetings/${editingMeetingId}`, payload);
        message.success('Meeting updated successfully');
      } else {
        await api.post('/meetings', payload);
        message.success('Meeting scheduled successfully');
      }

      setMeetingModalVisible(false);
      meetingForm.resetFields();
      fetchData();
    } catch (err) {
      console.error('Save meeting error:', err);
      message.error(err.response?.data?.message || 'Failed to save meeting');
    } finally {
      setMeetingSubmitting(false);
    }
  };

  const handleOpenUpdateMeetingStatus = (m) => {
    setSelectedMeeting(m);
    meetingStatusForm.resetFields();
    meetingStatusForm.setFieldsValue({
      status: m.status || 'SCHEDULE',
      remarks: m.remarks || ''
    });
    setMeetingStatusModalVisible(true);
  };

  const handleSubmitMeetingStatus = async (values) => {
    if (!selectedMeeting) return;
    try {
      setMeetingSubmitting(true);
      await api.patch(`/meetings/${selectedMeeting.id}/status`, {
        status: values.status,
        remarks: values.remarks || ''
      });
      message.success(`Meeting status updated to ${values.status}`);
      setMeetingStatusModalVisible(false);
      fetchData();
    } catch (err) {
      console.error('Update meeting status error:', err);
      message.error(err.response?.data?.message || 'Failed to update meeting status');
    } finally {
      setMeetingSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // 3. TICKET HANDLERS (Matching TicketScreen.js)
  // -----------------------------------------------------------------
  const handleOpenCreateTicket = () => {
    setIsEditTicket(false);
    setEditingTicketId(null);
    setTicketFile(null);
    ticketForm.resetFields();
    ticketForm.setFieldsValue({
      priority: 'MEDIUM',
      dueDate: dayjs().add(2, 'day')
    });
    setTicketModalVisible(true);
  };

  const handleOpenEditTicket = (t) => {
    setIsEditTicket(true);
    setEditingTicketId(t.id);
    setTicketFile(null);
    ticketForm.resetFields();
    ticketForm.setFieldsValue({
      title: t.title || '',
      description: t.description || '',
      priority: t.priority || 'MEDIUM',
      allocatedTo: t.allocatedTo || null,
      dueDate: t.dueDate ? dayjs(t.dueDate) : dayjs().add(2, 'day')
    });
    setTicketModalVisible(true);
  };

  const handleSaveTicket = async (values) => {
    try {
      setTicketSubmitting(true);
      const payload = {
        title: values.title.trim(),
        description: values.description.trim(),
        priority: values.priority || 'MEDIUM',
        allocatedTo: values.allocatedTo || null,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null
      };

      if (isEditTicket) {
        await api.patch(`/tickets/${editingTicketId}`, payload);
        message.success('Ticket updated successfully');
      } else {
        await api.post('/tickets', payload);
        message.success('Ticket raised successfully');
      }

      setTicketModalVisible(false);
      ticketForm.resetFields();
      setTicketFile(null);
      fetchData();
    } catch (err) {
      console.error('Save ticket error:', err);
      message.error(err.response?.data?.message || 'Failed to save ticket');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleOpenUpdateTicketStatus = (t) => {
    setSelectedTicket(t);
    ticketStatusForm.resetFields();
    ticketStatusForm.setFieldsValue({
      status: t.status || 'SCHEDULE',
      remarks: t.remarks || ''
    });
    setTicketStatusModalVisible(true);
  };

  const handleSubmitTicketStatus = async (values) => {
    if (!selectedTicket) return;
    try {
      setTicketSubmitting(true);
      await api.patch(`/tickets/${selectedTicket.id}/status`, {
        status: values.status,
        remarks: values.remarks || ''
      });
      message.success(`Ticket status updated to ${values.status}`);
      setTicketStatusModalVisible(false);
      fetchData();
    } catch (err) {
      console.error('Update ticket status error:', err);
      message.error(err.response?.data?.message || 'Failed to update ticket status');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleViewTicketDetail = (t) => {
    setViewingTicket(t);
    setTicketDetailModalVisible(true);
  };

  // Activity Table Columns
  const activityColumns = [
    {
      title: 'Task Title & Description',
      key: 'title',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1F2937', fontSize: 14 }}>{r.title}</div>
          <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2, maxWidth: 380 }}>{r.description}</div>
          {r.remarks && (
            <div style={{ color: '#9CA3AF', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
              Note: {r.remarks}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Target Date & Time',
      key: 'target',
      width: 180,
      render: (_, r) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
            <CalendarOutlined style={{ color: '#4F46E5' }} />
            <span>{r.turnAroundDate ? dayjs(r.turnAroundDate).format('DD MMM YYYY') : '--'}</span>
          </div>
          {r.turnAroundTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7280', marginTop: 2 }}>
              <ClockCircleOutlined />
              <span>{r.turnAroundTime}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const sConf = ACTIVITY_STATUS_OPTIONS.find(o => o.value === r.status) || ACTIVITY_STATUS_OPTIONS[0];
        return (
          <Tag
            color={r.status === 'DONE' ? 'green' : (r.status === 'IN_PROGRESS' ? 'blue' : (r.status === 'REVIEW' ? 'gold' : 'default'))}
            style={{ borderRadius: 6, fontWeight: 600, padding: '2px 8px' }}
          >
            {sConf.icon} {sConf.label}
          </Tag>
        );
      }
    },
    {
      title: 'Shared / Team',
      key: 'shared',
      width: 150,
      render: (_, r) => (
        r.transferredTo ? (
          <Tag color="cyan" style={{ borderRadius: 6 }}>
            {r.transferredTo.name || 'Team member'}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Self</Text>
        )
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 210,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            type="primary"
            ghost
            icon={<SettingOutlined />}
            onClick={() => handleOpenUpdateActivityStatus(r)}
            style={{ borderRadius: 6, fontSize: 12 }}
          >
            Status
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditActivity(r)}
            style={{ borderRadius: 6 }}
          />
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={() => handleOpenTransferActivity(r)}
            style={{ borderRadius: 6 }}
          />
        </Space>
      )
    }
  ];

  // Meeting Table Columns
  const meetingColumns = [
    {
      title: 'Meeting Agenda & Title',
      key: 'title',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1F2937', fontSize: 14 }}>{r.title}</div>
          <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>{r.description || 'No agenda specified'}</div>
        </div>
      )
    },
    {
      title: 'Scheduled Time',
      key: 'scheduledAt',
      width: 220,
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1E40AF', fontWeight: 500, fontSize: 12 }}>
          <CalendarOutlined />
          <span>{r.scheduledAt ? dayjs(r.scheduledAt).format('DD MMM YYYY, hh:mm A') : '--'}</span>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const s = r.status || 'SCHEDULE';
        const color = s === 'DONE' ? 'green' : (s === 'IN_PROGRESS' ? 'orange' : 'purple');
        return <Tag color={color} style={{ borderRadius: 6, fontWeight: 600 }}>{s}</Tag>;
      }
    },
    {
      title: 'Google Meet',
      key: 'link',
      width: 170,
      render: (_, r) => (
        r.meetLink ? (
          <Button
            type="primary"
            size="small"
            icon={<VideoCameraOutlined />}
            onClick={() => window.open(r.meetLink.startsWith('http') ? r.meetLink : `https://${r.meetLink}`, '_blank')}
            style={{ borderRadius: 6, background: '#059669', borderColor: '#059669', fontSize: 12 }}
          >
            Join Meet
          </Button>
        ) : (
          <Text type="secondary">No link</Text>
        )
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleOpenUpdateMeetingStatus(r)}
            style={{ borderRadius: 6 }}
          />
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditMeeting(r)}
            style={{ borderRadius: 6 }}
          />
        </Space>
      )
    }
  ];

  // Ticket Table Columns
  const ticketColumns = [
    {
      title: 'Ticket # & Subject',
      key: 'title',
      render: (_, r) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, color: '#4F46E5', fontSize: 12 }}>#{r.id}</span>
            <span style={{ fontWeight: 600, color: '#1F2937', fontSize: 14 }}>{r.title}</span>
          </div>
          <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>{r.description}</div>
        </div>
      )
    },
    {
      title: 'Priority',
      key: 'priority',
      width: 110,
      render: (_, r) => {
        const p = r.priority || 'MEDIUM';
        const color = p === 'HIGH' ? 'error' : (p === 'LOW' ? 'success' : 'warning');
        return <Tag color={color} style={{ borderRadius: 6, fontWeight: 600 }}>{p}</Tag>;
      }
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      width: 140,
      render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '--'
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (s) => (
        <Tag color={s === 'DONE' ? 'green' : (s === 'IN_PROGRESS' ? 'blue' : (s === 'REVIEW' ? 'gold' : 'purple'))} style={{ borderRadius: 6, fontWeight: 600 }}>
          {s || 'SCHEDULE'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 180,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            type="primary"
            ghost
            icon={<SettingOutlined />}
            onClick={() => handleOpenUpdateTicketStatus(r)}
            style={{ borderRadius: 6, fontSize: 12 }}
          >
            Status
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditTicket(r)}
            style={{ borderRadius: 6 }}
          />
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewTicketDetail(r)}
            style={{ borderRadius: 6 }}
          />
        </Space>
      )
    }
  ];

  return (
    <StaffLayout>
      {/* 1. TOP HEADER & PRIMARY ACTIONS */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 22,
            boxShadow: '0 8px 16px -4px rgba(79, 70, 229, 0.35)'
          }}>
            <CheckSquareOutlined />
          </div>
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
              Tasks & Activities
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Daily to-dos, scheduled meetings, and support tickets
            </Text>
          </div>
        </div>

        {/* Primary Action Buttons */}
        <Space size="middle" wrap>
          <Button
            type="default"
            icon={<SyncOutlined spin={loading} />}
            onClick={fetchData}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            Refresh
          </Button>

          <Button
            type="default"
            icon={<PlusOutlined style={{ color: '#4f46e5' }} />}
            onClick={handleOpenCreateActivity}
            style={{ borderRadius: 8, fontWeight: 600, borderColor: '#4f46e5', color: '#4f46e5' }}
          >
            New Activity
          </Button>

          <Button
            type="default"
            icon={<VideoCameraOutlined style={{ color: '#059669' }} />}
            onClick={handleOpenCreateMeeting}
            style={{ borderRadius: 8, fontWeight: 600, borderColor: '#059669', color: '#059669' }}
          >
            Schedule Meeting
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateTicket}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              borderColor: '#4f46e5',
              boxShadow: '0 4px 12px 0 rgba(79, 70, 229, 0.3)'
            }}
          >
            Raise Ticket
          </Button>
        </Space>
      </div>

      {/* 2. TOP METRIC STRIP (Compact, Elegant, Modern) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {/* Total Activities */}
        <Col xs={24} sm={12} lg={6}>
          <div
            onClick={() => { setActiveTab('activities'); setActivityFilter('ALL'); }}
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #E2E8F0',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>Active Tasks</span>
              <span style={{ background: '#EEF2FF', padding: '4px 8px', borderRadius: 6, color: '#4F46E5', fontSize: 11, fontWeight: 600 }}>
                This Month
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>{monthActivities.length}</span>
              <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>{activityCounts.DONE} completed</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Tag color="default" style={{ fontSize: 10, margin: 0 }}>{activityCounts.SCHEDULE} Scheduled</Tag>
              <Tag color="processing" style={{ fontSize: 10, margin: 0 }}>{activityCounts.IN_PROGRESS} In-Progress</Tag>
            </div>
          </div>
        </Col>

        {/* Scheduled Meetings */}
        <Col xs={24} sm={12} lg={6}>
          <div
            onClick={() => { setActiveTab('meetings'); setMeetingFilter('ALL'); }}
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #E2E8F0',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>Scheduled Meetings</span>
              <VideoCameraOutlined style={{ color: '#059669', fontSize: 16 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>{monthMeetings.length}</span>
              <span style={{ fontSize: 12, color: '#64748B' }}>conferences</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>{meetingCounts.SCHEDULE} Upcoming</Tag>
              <Tag color="success" style={{ fontSize: 10, margin: 0 }}>{meetingCounts.DONE} Done</Tag>
            </div>
          </div>
        </Col>

        {/* Support Tickets */}
        <Col xs={24} sm={12} lg={6}>
          <div
            onClick={() => { setActiveTab('tickets'); setTicketFilter('ALL'); }}
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #E2E8F0',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>Support Tickets</span>
              <FileTextOutlined style={{ color: '#D97706', fontSize: 16 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: '#0F172A' }}>{monthTickets.length}</span>
              <span style={{ fontSize: 12, color: '#64748B' }}>tickets</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>{ticketCounts.SCHEDULE + ticketCounts.IN_PROGRESS} Open</Tag>
              <Tag color="success" style={{ fontSize: 10, margin: 0 }}>{ticketCounts.DONE} Resolved</Tag>
            </div>
          </div>
        </Col>

        {/* Task Completion Progress */}
        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>Monthly Completion</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>{completionRate}%</span>
            </div>
            <div style={{ marginTop: 10 }}>
              <Progress
                percent={completionRate}
                showInfo={false}
                strokeColor={{ '0%': '#4F46E5', '100%': '#059669' }}
                trailColor="#F1F5F9"
                strokeWidth={8}
              />
            </div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
              {activityCounts.DONE} of {monthActivities.length} activities marked as completed
            </div>
          </div>
        </Col>
      </Row>

      {/* 3. MAIN WORKSPACE CARD */}
      <Card
        style={{
          borderRadius: 14,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="middle"
          items={[
            // ==========================================
            // TAB 1: ACTIVITIES (DEFAULT ACTIVE TAB)
            // ==========================================
            {
              key: 'activities',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <CheckSquareOutlined /> Activities & Tasks ({activities.length})
                </span>
              ),
              children: (
                <div>
                  {/* Filter & View Mode Bar */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16
                  }}>
                    <Space size="small" wrap>
                      {[
                        { key: 'ALL', label: 'All' },
                        { key: 'SCHEDULE', label: 'Scheduled' },
                        { key: 'IN_PROGRESS', label: 'In Progress' },
                        { key: 'REVIEW', label: 'In Review' },
                        { key: 'DONE', label: 'Completed' },
                      ].map((st) => (
                        <Button
                          key={st.key}
                          size="small"
                          type={activityFilter === st.key ? 'primary' : 'default'}
                          onClick={() => setActivityFilter(st.key)}
                          style={{
                            borderRadius: 6,
                            fontWeight: 600,
                            fontSize: 12,
                            height: 32,
                            padding: '0 12px'
                          }}
                        >
                          {st.label}
                        </Button>
                      ))}
                    </Space>

                    <Space size="middle" wrap>
                      <Input
                        placeholder="Search tasks..."
                        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                        value={activitySearch}
                        onChange={(e) => setActivitySearch(e.target.value)}
                        style={{ width: 220, borderRadius: 6, height: 34 }}
                        allowClear
                      />

                      <Radio.Group
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        size="small"
                        buttonStyle="solid"
                      >
                        <Radio.Button value="table"><UnorderedListOutlined /> List</Radio.Button>
                        <Radio.Button value="cards"><AppstoreOutlined /> Cards</Radio.Button>
                      </Radio.Group>
                    </Space>
                  </div>

                  {/* Render Table or Cards */}
                  {filteredActivities.length === 0 ? (
                    <Empty description="No activities found for this filter" style={{ padding: '40px 0' }} />
                  ) : viewMode === 'table' ? (
                    <Table
                      dataSource={filteredActivities}
                      rowKey="id"
                      columns={activityColumns}
                      pagination={{ pageSize: 8, showSizeChanger: true }}
                      size="middle"
                    />
                  ) : (
                    <Row gutter={[16, 16]}>
                      {filteredActivities.map((act) => {
                        const sConf = ACTIVITY_STATUS_OPTIONS.find(o => o.value === act.status) || ACTIVITY_STATUS_OPTIONS[0];

                        return (
                          <Col xs={24} md={12} lg={8} key={act.id}>
                            <div style={{
                              background: '#FFFFFF',
                              borderRadius: 10,
                              border: '1px solid #E5E7EB',
                              borderLeft: `4px solid ${sConf.color}`,
                              padding: '16px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              minHeight: 180
                            }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937' }}>
                                    {act.title}
                                  </div>
                                  <span style={{
                                    backgroundColor: sConf.bg,
                                    borderRadius: 4,
                                    padding: '2px 8px',
                                    border: `1px solid ${sConf.color}40`,
                                    color: sConf.color,
                                    fontSize: 10,
                                    fontWeight: 700
                                  }}>
                                    {sConf.label}
                                  </span>
                                </div>

                                <div style={{ fontSize: 12, color: '#4B5563', marginTop: 8, lineHeight: '18px' }}>
                                  {act.description}
                                </div>

                                {act.remarks && (
                                  <div style={{
                                    marginTop: 8,
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    background: '#F9FAFB',
                                    fontSize: 11,
                                    color: '#6B7280'
                                  }}>
                                    <strong>Remarks:</strong> {act.remarks}
                                  </div>
                                )}
                              </div>

                              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #F3F4F6' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7280' }}>
                                    <ClockCircleOutlined />
                                    <span>TAD: {act.turnAroundDate ? dayjs(act.turnAroundDate).format('DD MMM') : '--'}</span>
                                    {act.turnAroundTime && <span>({act.turnAroundTime})</span>}
                                  </div>

                                  {act.transferredTo && (
                                    <Tag color="cyan" style={{ fontSize: 10, borderRadius: 4 }}>
                                      Shared: {act.transferredTo.name || 'Member'}
                                    </Tag>
                                  )}
                                </div>

                                <div style={{ display: 'flex', gap: 6 }}>
                                  <Button
                                    size="small"
                                    type="primary"
                                    ghost
                                    icon={<SettingOutlined />}
                                    onClick={() => handleOpenUpdateActivityStatus(act)}
                                    style={{ fontSize: 11 }}
                                  >
                                    Status
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleOpenEditActivity(act)}
                                    style={{ fontSize: 11 }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<SwapOutlined />}
                                    onClick={() => handleOpenTransferActivity(act)}
                                    style={{ fontSize: 11 }}
                                  >
                                    Share
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )
            },

            // ==========================================
            // TAB 2: MEETINGS
            // ==========================================
            {
              key: 'meetings',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <VideoCameraOutlined /> Meetings ({meetings.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16
                  }}>
                    <Space size="small" wrap>
                      {[
                        { key: 'ALL', label: 'All' },
                        { key: 'SCHEDULE', label: 'Scheduled' },
                        { key: 'IN_PROGRESS', label: 'In Progress' },
                        { key: 'DONE', label: 'Completed' },
                      ].map((st) => (
                        <Button
                          key={st.key}
                          size="small"
                          type={meetingFilter === st.key ? 'primary' : 'default'}
                          onClick={() => setMeetingFilter(st.key)}
                          style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, height: 32, padding: '0 12px' }}
                        >
                          {st.label}
                        </Button>
                      ))}
                    </Space>

                    <Input
                      placeholder="Search meetings..."
                      prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                      value={meetingSearch}
                      onChange={(e) => setMeetingSearch(e.target.value)}
                      style={{ width: 240, borderRadius: 6, height: 34 }}
                      allowClear
                    />
                  </div>

                  {filteredMeetings.length === 0 ? (
                    <Empty description="No meetings scheduled" style={{ padding: '40px 0' }} />
                  ) : (
                    <Table
                      dataSource={filteredMeetings}
                      rowKey="id"
                      columns={meetingColumns}
                      pagination={{ pageSize: 8 }}
                      size="middle"
                    />
                  )}
                </div>
              )
            },

            // ==========================================
            // TAB 3: TICKETS
            // ==========================================
            {
              key: 'tickets',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <FileTextOutlined /> Support Tickets ({tickets.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16
                  }}>
                    <Space size="small" wrap>
                      {[
                        { key: 'ALL', label: 'All' },
                        { key: 'SCHEDULE', label: 'Scheduled' },
                        { key: 'IN_PROGRESS', label: 'In Progress' },
                        { key: 'REVIEW', label: 'In Review' },
                        { key: 'DONE', label: 'Resolved' },
                      ].map((st) => (
                        <Button
                          key={st.key}
                          size="small"
                          type={ticketFilter === st.key ? 'primary' : 'default'}
                          onClick={() => setTicketFilter(st.key)}
                          style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, height: 32, padding: '0 12px' }}
                        >
                          {st.label}
                        </Button>
                      ))}
                    </Space>

                    <Input
                      placeholder="Search tickets..."
                      prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                      value={ticketSearch}
                      onChange={(e) => setTicketSearch(e.target.value)}
                      style={{ width: 240, borderRadius: 6, height: 34 }}
                      allowClear
                    />
                  </div>

                  {filteredTickets.length === 0 ? (
                    <Empty description="No tickets found" style={{ padding: '40px 0' }} />
                  ) : (
                    <Table
                      dataSource={filteredTickets}
                      rowKey="id"
                      columns={ticketColumns}
                      pagination={{ pageSize: 8 }}
                      size="middle"
                    />
                  )}
                </div>
              )
            },

            // ==========================================
            // TAB 4: MONTHLY OVERVIEW (TodoListScreen.js)
            // ==========================================
            {
              key: 'overview',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <HourglassOutlined /> Monthly Overview
                </span>
              ),
              children: (
                <div style={{ paddingTop: 8 }}>
                  {/* Status Grid Breakdown */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                      Activities This Month
                    </div>
                    <Row gutter={[16, 16]}>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('activities'); setActivityFilter('SCHEDULE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #6366F1', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>📅</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{activityCounts.SCHEDULE}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Scheduled</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('activities'); setActivityFilter('IN_PROGRESS'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #F59E0B', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>⚙️</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{activityCounts.IN_PROGRESS}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>In-Progress</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('activities'); setActivityFilter('REVIEW'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #8B5CF6', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>🔍</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{activityCounts.REVIEW}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>In-Review</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('activities'); setActivityFilter('DONE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #10B981', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>✅</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{activityCounts.DONE}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Completed</div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                      Meetings & Tickets
                    </div>
                    <Row gutter={[16, 16]}>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('meetings'); setMeetingFilter('SCHEDULE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #6366F1', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>📅</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{meetingCounts.SCHEDULE}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Scheduled Meetings</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('meetings'); setMeetingFilter('DONE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #10B981', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>✅</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{meetingCounts.DONE}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Completed Meetings</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('tickets'); setTicketFilter('SCHEDULE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #F59E0B', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>⚠️</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{ticketCounts.SCHEDULE + ticketCounts.IN_PROGRESS}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Open Tickets</div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div
                          onClick={() => { setActiveTab('tickets'); setTicketFilter('DONE'); }}
                          style={{ background: '#F8FAFC', borderRadius: 8, padding: '14px', border: '1px solid #E2E8F0', borderLeft: '4px solid #10B981', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 18 }}>🎉</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', marginTop: 4 }}>{ticketCounts.DONE}</div>
                          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Resolved Tickets</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* ========================================================================= */}
      {/* 1. MODAL: CREATE / EDIT ACTIVITY                                          */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>{isEditActivity ? 'Edit Activity' : 'Create New Activity'}</span>}
        open={activityModalVisible}
        onCancel={() => setActivityModalVisible(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={activityForm}
          layout="vertical"
          onFinish={handleSaveActivity}
          style={{ marginTop: 12 }}
        >
          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Title *</span>}
            name="title"
            rules={[{ required: true, message: 'Please enter activity title' }]}
          >
            <Input placeholder="e.g. Client visit follow-up and documentation" style={{ borderRadius: 6, height: 40 }} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Description *</span>}
            name="description"
            rules={[{ required: true, message: 'Please enter activity description' }]}
          >
            <TextArea rows={3} placeholder="Provide details about this task..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, fontSize: 12 }}>Turn Around Date (TAD)</span>}
                name="turnAroundDate"
              >
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%', borderRadius: 6, height: 40 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, fontSize: 12 }}>Turn Around Time (TAT)</span>}
                name="turnAroundTime"
              >
                <TimePicker format="hh:mm A" use12Hours style={{ width: '100%', borderRadius: 6, height: 40 }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Remarks (Optional)</span>}
            name="remarks"
          >
            <Input placeholder="Additional notes or context" style={{ borderRadius: 6, height: 40 }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setActivityModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={activitySubmitting} style={{ borderRadius: 6, fontWeight: 600 }}>
              {isEditActivity ? 'Update Activity' : 'Save Activity'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 2. MODAL: UPDATE ACTIVITY STATUS                                          */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Update Activity Status</span>}
        open={activityStatusModalVisible}
        onCancel={() => setActivityStatusModalVisible(false)}
        footer={null}
        width={460}
        destroyOnClose
      >
        <Form
          form={activityStatusForm}
          layout="vertical"
          onFinish={handleSubmitActivityStatus}
          style={{ marginTop: 12 }}
        >
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Selected Task:</Text>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937' }}>{selectedActivity?.title}</div>
          </div>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Next Status</span>}
            name="status"
            rules={[{ required: true }]}
          >
            <Select style={{ height: 40, borderRadius: 6 }}>
              {ACTIVITY_STATUS_OPTIONS.map((st) => (
                <Option key={st.value} value={st.value}>
                  {st.icon} {st.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Remarks</span>}
            name="remarks"
          >
            <TextArea rows={2} placeholder="Explain progress or reason for status update..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setActivityStatusModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={activitySubmitting} style={{ borderRadius: 6, fontWeight: 600 }}>
              Update Status
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 3. MODAL: TRANSFER ACTIVITY                                               */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Share / Transfer Activity</span>}
        open={transferModalVisible}
        onCancel={() => setTransferModalVisible(false)}
        footer={null}
        width={460}
        destroyOnClose
      >
        <div style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 16, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Activity to Share:</Text>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937' }}>{transferringActivity?.title}</div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Select Team Member
            </div>
            <Select
              placeholder="Select staff member"
              value={targetUserId}
              onChange={setTargetUserId}
              style={{ width: '100%', height: 40 }}
              showSearch
              filterOption={(input, option) =>
                (option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {staffList.map((st) => (
                <Option key={st.id || st.userId} value={st.id || st.userId}>
                  {st.profile?.name || st.name || `Staff #${st.id || st.userId}`}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setTransferModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmitTransferActivity}
              loading={transferSubmitting}
              style={{ borderRadius: 6, fontWeight: 600 }}
            >
              Share Activity
            </Button>
          </div>
        </div>
      </Modal>

      {/* ========================================================================= */}
      {/* 4. MODAL: SCHEDULE / EDIT MEETING                                         */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>{isEditMeeting ? 'Edit Meeting' : 'Schedule Meeting'}</span>}
        open={meetingModalVisible}
        onCancel={() => setMeetingModalVisible(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={meetingForm}
          layout="vertical"
          onFinish={handleSaveMeeting}
          style={{ marginTop: 12 }}
        >
          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Meeting Title *</span>}
            name="title"
            rules={[{ required: true, message: 'Please enter meeting title' }]}
          >
            <Input placeholder="e.g. Weekly Operations Review" style={{ borderRadius: 6, height: 40 }} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Description / Agenda</span>}
            name="description"
          >
            <TextArea rows={2} placeholder="Key discussion points or meeting agenda..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Google Meet Link *</span>}
            name="meetLink"
            rules={[{ required: true, message: 'Google Meet link is mandatory' }]}
            help={<span style={{ fontSize: 11, color: '#6B7280' }}>Must be a valid Google Meet link (e.g. https://meet.google.com/abc-defg-hij)</span>}
          >
            <Input placeholder="https://meet.google.com/abc-defg-hij" style={{ borderRadius: 6, height: 40 }} prefix={<VideoCameraOutlined style={{ color: '#059669' }} />} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Scheduled Date and Time *</span>}
            name="scheduledAt"
            rules={[{ required: true, message: 'Please select scheduled time' }]}
          >
            <DatePicker
              showTime
              format="DD MMM YYYY, hh:mm A"
              style={{ width: '100%', borderRadius: 6, height: 40 }}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Attendees (Staff)</span>}
            name="attendees"
          >
            <Select
              mode="multiple"
              placeholder="Select attendees from staff list"
              style={{ width: '100%', borderRadius: 6 }}
              filterOption={(input, option) =>
                (option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {staffList.map((st) => (
                <Option key={st.id || st.userId} value={st.id || st.userId}>
                  {st.profile?.name || st.name || `Staff #${st.id || st.userId}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setMeetingModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={meetingSubmitting} style={{ borderRadius: 6, fontWeight: 600, background: '#059669', borderColor: '#059669' }}>
              {isEditMeeting ? 'Update Meeting' : 'Schedule Meeting'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 5. MODAL: UPDATE MEETING STATUS                                           */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Update Meeting Status</span>}
        open={meetingStatusModalVisible}
        onCancel={() => setMeetingStatusModalVisible(false)}
        footer={null}
        width={460}
        destroyOnClose
      >
        <Form
          form={meetingStatusForm}
          layout="vertical"
          onFinish={handleSubmitMeetingStatus}
          style={{ marginTop: 12 }}
        >
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Selected Meeting:</Text>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937' }}>{selectedMeeting?.title}</div>
          </div>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Status</span>}
            name="status"
            rules={[{ required: true }]}
          >
            <Select style={{ height: 40, borderRadius: 6 }}>
              {MEETING_STATUS_OPTIONS.map((st) => (
                <Option key={st.value} value={st.value}>
                  {st.icon} {st.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Remarks</span>}
            name="remarks"
          >
            <TextArea rows={2} placeholder="Meeting minutes or remarks..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setMeetingStatusModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={meetingSubmitting} style={{ borderRadius: 6, fontWeight: 600 }}>
              Save Status
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 6. MODAL: RAISE / EDIT TICKET                                             */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>{isEditTicket ? 'Edit Ticket' : 'Raise Support Ticket'}</span>}
        open={ticketModalVisible}
        onCancel={() => setTicketModalVisible(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={ticketForm}
          layout="vertical"
          onFinish={handleSaveTicket}
          style={{ marginTop: 12 }}
        >
          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Ticket Title *</span>}
            name="title"
            rules={[{ required: true, message: 'Please enter ticket title' }]}
          >
            <Input placeholder="e.g. Issue with client invoice balance" style={{ borderRadius: 6, height: 40 }} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Description *</span>}
            name="description"
            rules={[{ required: true, message: 'Please describe the ticket issue' }]}
          >
            <TextArea rows={3} placeholder="Provide complete details..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, fontSize: 12 }}>Priority</span>}
                name="priority"
                rules={[{ required: true }]}
              >
                <Select style={{ height: 40, borderRadius: 6 }}>
                  {PRIORITY_OPTIONS.map((p) => (
                    <Option key={p.value} value={p.value}>
                      <Tag color={p.color}>{p.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, fontSize: 12 }}>Due Date</span>}
                name="dueDate"
              >
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%', borderRadius: 6, height: 40 }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Allocate To (Team Member)</span>}
            name="allocatedTo"
          >
            <Select
              placeholder="Assign to staff member"
              style={{ width: '100%', height: 40 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {staffList.map((st) => (
                <Option key={st.id || st.userId} value={st.id || st.userId}>
                  {st.profile?.name || st.name || `Staff #${st.id || st.userId}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setTicketModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={ticketSubmitting} style={{ borderRadius: 6, fontWeight: 600, background: '#4F46E5', borderColor: '#4F46E5' }}>
              {isEditTicket ? 'Update Ticket' : 'Raise Ticket'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 7. MODAL: UPDATE TICKET STATUS                                            */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Update Ticket Status</span>}
        open={ticketStatusModalVisible}
        onCancel={() => setTicketStatusModalVisible(false)}
        footer={null}
        width={460}
        destroyOnClose
      >
        <Form
          form={ticketStatusForm}
          layout="vertical"
          onFinish={handleSubmitTicketStatus}
          style={{ marginTop: 12 }}
        >
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Ticket #{selectedTicket?.id}:</Text>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937' }}>{selectedTicket?.title}</div>
          </div>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Status</span>}
            name="status"
            rules={[{ required: true }]}
          >
            <Select style={{ height: 40, borderRadius: 6 }}>
              {TICKET_STATUS_OPTIONS.map((st) => (
                <Option key={st.value} value={st.value}>
                  {st.icon} {st.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 600, fontSize: 12 }}>Remarks</span>}
            name="remarks"
          >
            <TextArea rows={2} placeholder="Resolution notes or remarks..." style={{ borderRadius: 6 }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => setTicketStatusModalVisible(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={ticketSubmitting} style={{ borderRadius: 6, fontWeight: 600 }}>
              Update Status
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ========================================================================= */}
      {/* 8. MODAL: TICKET DETAILS                                                  */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Ticket Details #{viewingTicket?.id}</span>}
        open={ticketDetailModalVisible}
        onCancel={() => setTicketDetailModalVisible(false)}
        footer={null}
        width={560}
      >
        {viewingTicket && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>{viewingTicket.title}</div>
              <Space>
                <Tag color={viewingTicket.priority === 'HIGH' ? '#EF4444' : (viewingTicket.priority === 'LOW' ? '#10B981' : '#F59E0B')}>
                  {viewingTicket.priority || 'MEDIUM'}
                </Tag>
                <Tag color="blue">{viewingTicket.status || 'SCHEDULE'}</Tag>
              </Space>
            </div>

            <div style={{ marginBottom: 16, color: '#4B5563', lineHeight: '20px' }}>
              {viewingTicket.description}
            </div>

            <Divider style={{ margin: '14px 0' }} />

            <Row gutter={[16, 12]} style={{ fontSize: 12 }}>
              <Col span={12}>
                <span style={{ color: '#9CA3AF' }}>Due Date:</span>{' '}
                <strong>{viewingTicket.dueDate ? dayjs(viewingTicket.dueDate).format('DD MMM YYYY') : '--'}</strong>
              </Col>
              <Col span={12}>
                <span style={{ color: '#9CA3AF' }}>Created At:</span>{' '}
                <strong>{viewingTicket.createdAt ? dayjs(viewingTicket.createdAt).format('DD MMM YYYY') : '--'}</strong>
              </Col>
              {viewingTicket.allocatedToUser?.name && (
                <Col span={24}>
                  <span style={{ color: '#9CA3AF' }}>Allocated To:</span>{' '}
                  <strong>{viewingTicket.allocatedToUser.name}</strong>
                </Col>
              )}
            </Row>

            {viewingTicket.remarks && (
              <div style={{ marginTop: 14, background: '#F9FAFB', padding: '10px 14px', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280' }}>Latest Remarks:</div>
                <div style={{ fontSize: 12, color: '#1F2937', marginTop: 2 }}>{viewingTicket.remarks}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </StaffLayout>
  );
};

export default StaffTasks;
